'use client'

import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import {
  BlogAppCTA,
  BlogComparisonTable,
  BlogFAQ,
  BlogKeyTakeaways,
  BlogLastUpdated,
  BlogSources,
  BlogTLDR,
  BlogWhatTheAppChecks,
} from '@/app/components/blog/BlogAeo'
import KeyStats from '@/app/components/blog/KeyStats'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Evian Have Microplastics? What Two Tested Lots Found"
      author="MicroPlastics Research Desk"
      date="August 29, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/does-evian-have-microplastics.webp"
      heroImageAlt="Clear mineral water bottle with a pale pink cap beside laboratory glassware and an Alpine backdrop"
    >
      <BlogLastUpdated iso="2026-09-03" formatted="September 3, 2026" />

      <BlogTLDR>
        <strong>Yes—in the only peer-reviewed brand-specific survey we found, both Evian lots contained particles detected by the study&apos;s method.</strong>{' '}
        The lots averaged about <strong>197 and 58 particles per litre</strong>.
        That fourfold gap is the most useful finding: &ldquo;Evian&rdquo; is not one
        permanent particle count. These were bottles purchased in 2017, and most
        particles below 100 µm were screened with Nile Red fluorescence rather
        than individually confirmed by spectroscopy. The study therefore proves
        that those lots tested positive under that method—not what is inside
        every Evian bottle sold today. Evian&apos;s current plastic bottle is PET,
        now made with recycled PET in several markets. If Evian is your daily
        water, changing the <strong>container system</strong> matters more than
        searching for a supposedly particle-free PET brand.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>Evian was named in a peer-reviewed test.</strong> Many brand pages quietly substitute a generic bottled-water average.</>,
          <><strong>Two lots gave different answers:</strong> approximately 197 and 58 particles/L under the same published method.</>,
          <>The paper analysed <strong>259 bottles across 11 brands</strong>; 93% showed signs of microplastic contamination.</>,
          <>The 6.5–100 µm fraction was Nile Red-screened; larger particles received limited FTIR confirmation.</>,
          <>Evian says its current plastic bottles in Europe, the U.S. and Canada use 100% recycled PET, excluding caps and labels. Recycled PET is still PET.</>,
          <><strong>Practical move:</strong> use filtered tap water in glass or unlined stainless steel when local water quality allows.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_evian" />

      <KeyStats
        heading="Evian microplastics: the numbers with their limits attached"
        stats={[
          {
            value: '197/L',
            label: 'average in Evian lot 1',
            context: '26.0/L over 100 µm plus 171/L in the 6.5–100 µm screened fraction',
            source: 'Mason et al. 2018, Table 3',
            sourceUrl: 'https://www.frontiersin.org/journals/chemistry/articles/10.3389/fchem.2018.00407/full',
          },
          {
            value: '58.2/L',
            label: 'average in Evian lot 2',
            context: '1.51/L over 100 µm plus 56.7/L in the smaller screened fraction',
            source: 'Mason et al. 2018, Table 3',
            sourceUrl: 'https://www.frontiersin.org/journals/chemistry/articles/10.3389/fchem.2018.00407/full',
          },
          {
            value: '93%',
            label: 'of all bottles showed contamination',
            context: '259 bottles, 11 brands, 27 lots and nine countries—not an Evian-only rate',
            source: 'Mason et al. 2018',
            sourceUrl: 'https://doi.org/10.3389/fchem.2018.00407',
          },
          {
            value: '6.5 µm',
            label: 'lower size cutoff',
            context: 'particles below that threshold were outside the study count',
            source: 'Mason et al. 2018 methods',
            sourceUrl: 'https://doi.org/10.3389/fchem.2018.00407',
          },
        ]}
      />

      <h2>Does Evian water contain microplastics?</h2>
      <p>
        <strong>Both tested Evian lots did.</strong> Mason, Welch and Neratko
        bought one lot through Amazon and another in Fredonia, New York, then
        screened multiple bottles from each. Table 3 reports a lot average of
        197 particles/L for the first and 58.2 particles/L for the second after
        blank correction. Individual bottles ranged from 0 to 256 particles/L.
      </p>
      <p>
        Those numbers are more defensible than applying the often-quoted 2024
        bottled-water average of roughly 240,000 particles/L directly to Evian.
        That later PNAS study used a far more sensitive technique and did not
        disclose its three brands. It is excellent evidence that analytical
        methods miss nanoplastics; it is <strong>not</strong> an Evian test.
      </p>

      <BlogComparisonTable
        caption="The two published Evian lots (Mason et al., 2018, Table 3)"
        columns={[
          { key: 'lot', label: 'Evian lot' },
          { key: 'large', label: '>100 µm, FTIR-supported' },
          { key: 'small', label: '6.5–100 µm, Nile Red-screened' },
          { key: 'total', label: 'Reported average' },
        ]}
        rows={[
          { lot: 'PRD 03 21 2017 14:02', large: '26.0/L', small: '171/L', total: '197/L' },
          { lot: 'PRD 05 24 17 11:29', large: '1.51/L', small: '56.7/L', total: '58.2/L' },
        ]}
      />

      <p>
        The fourfold difference does not show that one retailer was better. It
        shows why lot, handling, filling conditions and ordinary sampling
        variation matter. A brand result without a lot and method is incomplete.
      </p>

      <h2>How reliable are the 197 and 58 particles/L figures?</h2>
      <p>
        They are reliable descriptions of the paper&apos;s result, with an important
        methods label. Particles above 100 µm were inspected with Fourier-transform
        infrared spectroscopy, and half of the analysed larger particles were
        confirmed as polymeric. The much more numerous 6.5–100 µm particles were
        counted after Nile Red staining because the study could not chemically
        identify each one at that size.
      </p>
      <p>
        Nile Red binds to hydrophobic material and is useful for screening, but
        it is not identical to polymer identification. The paper supports the
        presence of microplastic contamination while leaving uncertainty around
        the identity of every small fluorescent particle. Our guide to{' '}
        <Link href="/blog/why-water-tests-miss-small-microplastics" className="text-forest underline">
          why water tests report radically different particle counts
        </Link>{' '}
        explains how the lower size cutoff changes the headline.
      </p>

      <h2>What plastic is an Evian bottle made from?</h2>
      <p>
        Evian identifies the bottle body as polyethylene terephthalate, or PET
        (resin code 1). Its international packaging page says plastic bottles in
        Europe, the United States and Canada are now made from 100% recycled PET,
        excluding the cap and label. The U.S. page dates that change to May 2024
        for newly manufactured U.S. bottles.
      </p>
      <p>
        Recycled content is relevant to virgin-plastic demand and packaging
        circularity. It is not a microplastic-free certification. The bottle body
        remains PET, the cap remains a separate material, and neither brand page
        publishes a final-bottle particle result. Do not turn an environmental
        packaging claim into a laboratory claim the company did not make.
      </p>

      <h2>Is glass-bottled Evian microplastic-free?</h2>
      <p>
        Glass removes the PET bottle wall, which is a meaningful reduction in
        plastic contact. It does not create a zero-particle guarantee because the
        closure, filling line and handling remain. In a 2018 German study of
        multiple mineral-water packages—not Evian specifically—glass bottles
        averaged 50 ± 52 particles/L, while single-use PET averaged 14 ± 14/L
        and returnable PET 118 ± 88/L. Packaging format alone did not perfectly
        predict the result.
      </p>
      <p>
        That counterintuitive finding is why we rank options by the whole contact
        path rather than the word &ldquo;glass.&rdquo; It is still reasonable to choose
        glass Evian over PET when both are available; it is not reasonable to
        promise that the glass version contains zero microplastics.
      </p>

      <BlogComparisonTable
        caption="How to choose an everyday water format"
        columns={[
          { key: 'option', label: 'Option' },
          { key: 'plasticPath', label: 'Main plastic-contact path' },
          { key: 'evidence', label: 'What the evidence supports' },
          { key: 'use', label: 'Best use' },
        ]}
        rows={[
          { option: 'Filtered tap + glass/unlined steel', plasticPath: 'Filter parts and reusable closure', evidence: 'Removes the single-use PET body; performance depends on the chosen filter', use: 'Best everyday default where tap is suitable' },
          { option: 'Evian in glass', plasticPath: 'Cap/liner and bottling system', evidence: 'Lower bottle-wall contact; no current Evian format comparison', use: 'Occasional bottled option' },
          { option: 'Evian in PET', plasticPath: 'PET bottle + cap', evidence: 'Two 2017 lots tested positive under Mason 2018', use: 'Convenience or travel' },
          { option: 'Heat-exposed or repeatedly reused PET', plasticPath: 'PET body + cap + added wear/heat', evidence: 'Avoidable stress without a demonstrated benefit', use: 'Replace rather than make routine' },
        ]}
      />

      <h2>Is Evian safer than other bottled water brands?</h2>
      <p>
        The published microplastic evidence does not support a safety ranking.
        Mason&apos;s survey found contamination across all brands and lots tested,
        with large variation inside brands. No health-based regulatory limit
        lets us convert 58 or 197 particles/L into a medical verdict. Evian&apos;s
        protected mineral source and composition are separate questions from
        what the package and bottling process can contribute.
      </p>
      <p>
        If you are comparing labels, use our{' '}
        <Link href="/blog/microplastics-bottled-water-brands-ranked" className="text-forest underline">
          bottled-water brand evidence guide
        </Link>{' '}
        and the underlying{' '}
        <Link href="/data/bottled-water-brand-database" className="text-forest underline">
          bottled-water database
        </Link>.
        The useful distinction is measured brand result versus category-level
        inference—not premium versus budget branding.
      </p>

      <BlogAppCTA
        headline="Check the exact water package in your hand"
        sub="Scan the bottle format, cap, condition and storage pattern. The app builds a modeled packaging-contact assessment and suggests a lower-contact option; it does not pretend to lab-test the water through your camera."
        buttonLabel="Scan my water bottle"
        source="blog_evian_inline_cta"
        postSlug="does-evian-have-microplastics"
        queryFamily="does_evian_have_microplastics"
      />

      <h2>What should a regular Evian drinker do?</h2>
      <ol className="list-decimal space-y-2 pl-6">
        <li><strong>Do not panic over one bottle.</strong> The evidence supports a packaging pattern, not an acute health conclusion.</li>
        <li><strong>Change the daily system first.</strong> Compare your local water report and a filter suited to the contaminants actually present.</li>
        <li><strong>Use glass or unlined stainless steel for storage.</strong> That removes the single-use PET bottle body from repeated daily contact.</li>
        <li><strong>Keep bottled water cool.</strong> Do not make a hot car or sunny window the storage place for a case of PET bottles.</li>
        <li><strong>Do not repeatedly reuse a damaged single-use bottle.</strong> Retire it when crumpled, scratched or difficult to clean.</li>
      </ol>
      <p>
        Start with our{' '}
        <Link href="/blog/microplastics-water-filters-compared" className="text-forest underline">
          water-filter comparison
        </Link>{' '}
        and our evidence review of{' '}
        <Link href="/blog/tap-water-vs-bottled-water-microplastics" className="text-forest underline">
          tap versus bottled water
        </Link>.
        If your concern is a case stored in summer heat, use the dedicated{' '}
        <Link href="/blog/water-bottle-left-in-hot-car-microplastics" className="text-forest underline">
          hot-car bottled-water guide
        </Link>.
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Whether the exact package is PET, recycled PET or glass—not just the Evian brand name.</>,
          <>Bottle condition, cap, storage heat, reuse and frequency.</>,
          <>Comparable bottled, tap, filtered and reusable-container options.</>,
          <>A modeled packaging-contact signal—not a measurement of particles in your bottle or your body.</>,
        ]}
      />

      <BlogFAQ
        items={[
          {
            question: 'Does Evian water have microplastics?',
            answer: 'Yes in the two Evian lots tested in Mason et al. 2018. They averaged about 197 and 58 particles per litre under a method with a 6.5 µm lower cutoff. Those 2017 lots do not establish the count in every bottle sold today.',
          },
          {
            question: 'How many microplastics are in Evian water?',
            answer: 'There is no single current Evian count. The only widely cited brand-specific study reported two different lot averages—197 and 58.2 particles/L—showing why the lot, date and analytical method must accompany the number.',
          },
          {
            question: 'Is an Evian bottle PET plastic?',
            answer: 'Yes. Evian identifies its plastic bottle body as PET. The company says bottles in Europe, the U.S. and Canada now use 100% recycled PET, excluding caps and labels.',
          },
          {
            question: 'Does recycled PET stop microplastic shedding?',
            answer: 'No published Evian test establishes that. Recycled content can reduce demand for virgin resin, but the finished bottle remains PET and recycled content is not a zero-particle certification.',
          },
          {
            question: 'Is Evian in glass microplastic-free?',
            answer: 'Glass removes the PET bottle wall, but caps, liners and bottling equipment remain. No current Evian glass-versus-PET study proves that the glass version contains zero plastic particles.',
          },
          {
            question: 'What is the lower-plastic-contact alternative to Evian?',
            answer: 'Where local water quality permits, use a filter selected for your water report and store the result in glass or unlined stainless steel. That changes the recurring package instead of guessing among PET brands.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Mason SA, Welch VG, Neratko J', year: 2018, title: 'Synthetic Polymer Contamination in Bottled Water', publisher: 'Frontiers in Chemistry', url: 'https://doi.org/10.3389/fchem.2018.00407' },
          { authors: 'Schymanski D, Goldbeck C, Humpf HU, Fürst P', year: 2018, title: 'Analysis of microplastics in water by micro-Raman spectroscopy: Release of plastic particles from different packaging into mineral water', publisher: 'Water Research', url: 'https://pubmed.ncbi.nlm.nih.gov/29145085/' },
          { authors: 'Qian N, et al.', year: 2024, title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy', publisher: 'Proceedings of the National Academy of Sciences', url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121' },
          { authors: 'Evian', year: 2026, title: 'Adopting a Circular Model', publisher: 'Evian official website', url: 'https://www.evian.com/en_int/our-sustainability-actions/adopting-a-circular-model/' },
          { authors: 'Evian', year: 2026, title: 'Packaging & Recycling: Recycled PET Bottle', publisher: 'Evian U.S. official website', url: 'https://www.evian.com/en_us/sustainable-bottled-water/water-bottle-recycling-and-packaging/' },
        ]}
      />
    </BlogPostLayout>
  )
}
